import { useMap } from "@vis.gl/react-google-maps";
import { useCallback, useMemo } from "react";

import { useSupercluster } from "@/hooks/useSupercluster";
import AppMapCluster from "./AppMapCluster";
import AppMapMarker from "./AppMapMarker";

const superclusterOptions = {
  extent: 256,
  radius: 80,
  maxZoom: 14,
};

// Clusters use their point count as z-index, so address pins sit above any of
// them when they overlap.
const ADDRESS_Z_INDEX = 1_000_000;

export function ClusteredMarkers({ geojson }) {
  const map = useMap();

  // An investigator needs to see where the case's own addresses are at any
  // zoom, so address markers are kept out of the clustering pass entirely.
  const { addressFeatures, clusterableGeojson } = useMemo(() => {
    const addresses = [];
    const clusterable = [];

    geojson.features.forEach(feature => {
      if (feature.properties.marker.type === "address") {
        addresses.push(feature);
      } else {
        clusterable.push(feature);
      }
    });

    return {
      addressFeatures: addresses,
      clusterableGeojson: { type: "FeatureCollection", features: clusterable },
    };
  }, [geojson]);

  const { clusters, getClusterExpansionZoom } = useSupercluster(
    clusterableGeojson,
    superclusterOptions
  );

  const handleClusterClick = useCallback(
    (clusterId, position) => {
      map.setZoom(getClusterExpansionZoom(clusterId));
      map.panTo(position);
    },
    [map, getClusterExpansionZoom]
  );

  return (
    <>
      {addressFeatures.map(feature => (
        <AppMapMarker
          key={`address-${feature.id}`}
          markerData={feature.properties.marker}
          zIndex={ADDRESS_Z_INDEX}
        />
      ))}
      {clusters.map(feature => {
        const [lng, lat] = feature.geometry.coordinates;
        const position = { lat, lng };
        const { cluster, cluster_id, point_count, point_count_abbreviated } =
          feature.properties;

        if (cluster) {
          return (
            <AppMapCluster
              key={`cluster-${cluster_id}`}
              position={position}
              count={point_count}
              label={String(point_count_abbreviated)}
              onClick={() => handleClusterClick(cluster_id, position)}
            />
          );
        }

        return (
          <AppMapMarker
            key={`feature-${feature.id}`}
            markerData={feature.properties.marker}
          />
        );
      })}
    </>
  );
}
